import { Box, HStack, VStack } from '@chakra-ui/react';
import { ColumnDef, ColumnFiltersState, SortingFn, SortingState, Table, VisibilityState } from '@tanstack/react-table';
import { produce, WritableDraft } from 'immer';
import React, { ReactNode } from 'react';
import { useImmerReducer } from 'use-immer';

import ColorPickerDialog from '@/components/color-picker-dialog/ColorPickerDialog';
import VirtualTable from '@/components/virtual-table/VirtualTable';
import {
  DEFAULT_ARRAY_START_INDEX,
  DEFAULT_ONE_ELEMENT_ARRAY_LENGTH,
  DEFAULT_TABLE_WIDTH_MD,
} from '@/constants/defaults';
import { FULL_WIDTH } from '@/constants/ui';
import { ELEMENT_DATA_TEST_IDS } from '@/tests/testConstants';
import {
  APLItem,
  CellColor,
  CellColorUpdate,
  ColorCell,
  ColorMode,
  ColorPickerState,
  HeaderSortHandlers,
} from '@/types/ui.types';

export type CellColors = Omit<ColorPickerState, 'colorMode' | 'headerText' | 'isOpen'>;

interface PaginatedPreviewTableProps<T> {
  availableItemsTableColumnFilters: ColumnFiltersState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableItemsTableColumns: ColumnDef<any, any>[];
  availableItemsTableData: unknown[];
  availableRowUniqueKey: string;
  availableTableUniqueSelectedIds?: Set<number | string>;
  isLoading: boolean;
  previewRowUniqueKey: string;
  previewTableColumnFilters: ColumnFiltersState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previewTableColumns: ColumnDef<any, any>[];
  previewTableData: unknown[];
  previewTableUpdateHandler: (updatedData: unknown[]) => void;
  availableItemsTableColumnVisibility?: VisibilityState;
  availableItemsTableHeader?: ReactNode;
  availableItemsTableHeaderButton?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableItemsTableInstanceRef?: React.MutableRefObject<Table<any> | undefined>;
  availableItemsTableNoDataFoundMessage?: string;
  availableItemsTableSortingState?: SortingState;
  handleSetCellColors?: (colors: CellColorUpdate) => void;
  headerSortHandlers?: HeaderSortHandlers;
  hideSortingTable?: boolean;
  overrideTableSorting?: boolean;
  previewHeaderSortHandlers?: HeaderSortHandlers;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previewItemsTableInstanceRef?: React.MutableRefObject<Table<any> | undefined>;
  previewTableColumnVisibility?: VisibilityState;
  previewTableHeader?: ReactNode;
  previewTableHeaderButton?: ReactNode;
  previewTableNoDataFoundMessage?: string;
  previewTableSortingState?: SortingState;
  previewTableUniqueSelectedIds?: Set<number | string>;
  selectedPreviewItems?: APLItem<T>[];
  showColorPicker?: boolean;
  showSideControls?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortingFns?: Record<string, SortingFn<any>>;
  topPosition?: string;
}

const DEFAULT_COLOR_PICKER_STATE: ColorPickerState = {
  cellColor: '', // Default to empty for transparent background
  cellColorChanged: false,
  colorMode: ColorMode.Cell,
  headerText: '',
  isOpen: false,
  textColor: '', // Default to empty to hide headerText initially
  textColorChanged: false,
};

interface UIState {
  colorPickerState: ColorPickerState;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ITableState {
  uiState: UIState;
}

enum ReducerActionTypes {
  SET_COLOR_PICKER_IS_OPEN,
  SET_COLOR_PICKER_STATE,
}

type ReducerAction = { type: ReducerActionTypes; payload?: unknown };

const stateReducer = (draft: WritableDraft<ITableState>, action: ReducerAction): void => {
  switch (action.type) {
    case ReducerActionTypes.SET_COLOR_PICKER_IS_OPEN:
      draft.uiState.colorPickerState.isOpen = action.payload as boolean;
      break;
    case ReducerActionTypes.SET_COLOR_PICKER_STATE:
      draft.uiState.colorPickerState = action.payload as ColorPickerState;
      break;
    default:
      break;
  }
};

// Type guard for ColorCell[]
const isColorCellArray = (item: unknown): item is ColorCell[] => {
  const itemAsColorCellArray = item as ColorCell[];

  return (
    Array.isArray(itemAsColorCellArray) &&
    itemAsColorCellArray.every((target) => {
      const targetAsColorCell = target as ColorCell;

      return (
        typeof targetAsColorCell === 'object' &&
        targetAsColorCell !== null &&
        (typeof targetAsColorCell?.color === 'string' || targetAsColorCell?.color === null) &&
        (typeof targetAsColorCell?.colorText === 'string' || targetAsColorCell?.colorText === null)
      );
    })
  );
};

const PaginatedPreviewTable = <T,>(props: PaginatedPreviewTableProps<T>): React.JSX.Element => {
  const {
    availableItemsTableColumnFilters,
    availableItemsTableColumns,
    availableItemsTableColumnVisibility = undefined,
    availableItemsTableData,
    availableItemsTableHeader = null,
    availableItemsTableHeaderButton = null,
    availableItemsTableInstanceRef = undefined,
    availableItemsTableNoDataFoundMessage = undefined,
    availableItemsTableSortingState,
    availableRowUniqueKey,
    availableTableUniqueSelectedIds = new Set<number>(),
    handleSetCellColors = () => void {},
    headerSortHandlers,
    hideSortingTable,
    isLoading,
    overrideTableSorting = false,
    previewHeaderSortHandlers,
    previewItemsTableInstanceRef = undefined,
    previewRowUniqueKey,
    previewTableColumnFilters,
    previewTableColumns,
    previewTableColumnVisibility = undefined,
    previewTableData,
    previewTableHeader = null,
    previewTableHeaderButton = null,
    previewTableNoDataFoundMessage = undefined,
    previewTableSortingState,
    previewTableUniqueSelectedIds = new Set<number>(),
    previewTableUpdateHandler,
    selectedPreviewItems = undefined,
    showColorPicker = true,
    showSideControls = true,
    sortingFns = undefined,
    topPosition = undefined,
  } = props;

  const [tableState, dispatchTableState] = useImmerReducer<ITableState, ReducerAction>(stateReducer, {
    uiState: {
      colorPickerState: DEFAULT_COLOR_PICKER_STATE,
    },
  });

  // This is needed to display the correct colors and header text when opening Color Picker Dialog
  const handleColorPicker = React.useCallback(() => {
    if (selectedPreviewItems?.length && isColorCellArray(selectedPreviewItems)) {
      // If the user selects just 1 item, set the colors and the name of that item on the Color Picker Dialog
      if (selectedPreviewItems.length === DEFAULT_ONE_ELEMENT_ARRAY_LENGTH) {
        const selectedItemColors: CellColor = {
          cellColor: selectedPreviewItems[DEFAULT_ARRAY_START_INDEX].color ?? DEFAULT_COLOR_PICKER_STATE.cellColor,
          textColor: selectedPreviewItems[DEFAULT_ARRAY_START_INDEX].colorText ?? DEFAULT_COLOR_PICKER_STATE.textColor,
        };
        const headerText = selectedPreviewItems[DEFAULT_ARRAY_START_INDEX].name;

        dispatchTableState({
          payload: produce(tableState.uiState.colorPickerState, (draft) => {
            draft.cellColor = selectedItemColors.cellColor;
            draft.colorMode = ColorMode.Cell;
            draft.headerText = headerText;
            draft.isOpen = true;
            draft.textColor = selectedItemColors.textColor;
          }),
          type: ReducerActionTypes.SET_COLOR_PICKER_STATE,
        });
      } else {
        // check if the selection has the same cell and text color (and is not null)
        // in this case we should display the header text with the group selected colors
        const normalize = (value: string | null | undefined) => value ?? '';

        const [firstItem] = selectedPreviewItems;
        const firstCellColor = normalize(firstItem?.color);
        const firstTextColor = normalize(firstItem?.colorText);

        const allColorsSame = selectedPreviewItems.every(
          (item) => normalize(item.color) === firstCellColor && normalize(item.colorText) === firstTextColor,
        );

        // Display the selected item name or the number of the selected items on Color Picker Dialog header
        dispatchTableState({
          payload: produce(tableState.uiState.colorPickerState, (draft) => {
            Object.assign(draft, DEFAULT_COLOR_PICKER_STATE);
            if (allColorsSame) {
              draft.cellColor = firstCellColor ?? DEFAULT_COLOR_PICKER_STATE.cellColor;
              draft.textColor = firstTextColor ?? DEFAULT_COLOR_PICKER_STATE.textColor;
            }
            draft.colorMode = ColorMode.Cell;
            draft.headerText = `${selectedPreviewItems.length} Selected`;
            draft.isOpen = true;
          }),
          type: ReducerActionTypes.SET_COLOR_PICKER_STATE,
        });
      }
    }
  }, [dispatchTableState, selectedPreviewItems, tableState.uiState.colorPickerState]);

  const handleConfirmColors = () => {
    dispatchTableState({
      payload: false,
      type: ReducerActionTypes.SET_COLOR_PICKER_IS_OPEN,
    });

    const { cellColor, textColor, textColorChanged, cellColorChanged } = tableState.uiState.colorPickerState;

    handleSetCellColors({
      cellColor,
      cellColorChanged,
      textColor,
      textColorChanged,
    });
  };

  const clearColor = () => {
    if (tableState.uiState.colorPickerState.colorMode === ColorMode.Cell) {
      dispatchTableState({
        payload: produce(tableState.uiState.colorPickerState, (draft) => {
          draft.cellColor = '';
          draft.cellColorChanged = true;
        }),
        type: ReducerActionTypes.SET_COLOR_PICKER_STATE,
      });
    } else {
      dispatchTableState({
        payload: produce(tableState.uiState.colorPickerState, (draft) => {
          draft.textColor = '';
          draft.textColorChanged = true;
        }),
        type: ReducerActionTypes.SET_COLOR_PICKER_STATE,
      });
    }
  };

  const handleQuickColorPicker = (color: CellColor) => {
    const { cellColor, textColor } = color;

    handleSetCellColors({
      cellColor,
      cellColorChanged: true,
      textColor,
      textColorChanged: true,
    });
  };

  const handleClose = () => {
    dispatchTableState({
      payload: produce(tableState.uiState.colorPickerState, (draft) => {
        Object.assign(draft, DEFAULT_COLOR_PICKER_STATE);
        draft.isOpen = false;
      }),
      type: ReducerActionTypes.SET_COLOR_PICKER_STATE,
    });
  };

  return (
    <Box w={FULL_WIDTH}>
      {tableState.uiState.colorPickerState.isOpen && (
        <ColorPickerDialog
          colorPickerState={tableState.uiState.colorPickerState}
          headerText={tableState.uiState.colorPickerState.headerText}
          isOpen={tableState.uiState.colorPickerState.isOpen}
          onClear={clearColor}
          onClose={handleClose}
          onConfirm={handleConfirmColors}
          onStateChange={(colorPickerState) =>
            dispatchTableState({
              payload: colorPickerState,
              type: ReducerActionTypes.SET_COLOR_PICKER_STATE,
            })
          }
        />
      )}
      <VStack align={'left'}>
        <Box>
          <HStack minW={`${DEFAULT_TABLE_WIDTH_MD}px`}>
            <HStack justifyContent={'space-between'} align={'top'} w={'100%'} gap={4}>
              <VirtualTable
                columnFilters={availableItemsTableColumnFilters}
                columns={availableItemsTableColumns}
                columnVisibility={availableItemsTableColumnVisibility}
                data={availableItemsTableData ?? []}
                dataTestId={ELEMENT_DATA_TEST_IDS.AVAILABLE_TABLE_BODY}
                handleUpdateOrder={previewTableUpdateHandler}
                headerSortHandlers={headerSortHandlers}
                isLoading={isLoading}
                noDataFoundMessage={availableItemsTableNoDataFoundMessage}
                sortingState={availableItemsTableSortingState}
                TableHeader={availableItemsTableHeader}
                TableHeaderButton={availableItemsTableHeaderButton}
                tableInstanceRef={availableItemsTableInstanceRef}
                topPosition={topPosition}
                uniqueRowKey={availableRowUniqueKey}
                uniqueSelectedIds={availableTableUniqueSelectedIds}
              />

              <VirtualTable
                columnFilters={previewTableColumnFilters}
                columns={previewTableColumns}
                columnVisibility={previewTableColumnVisibility}
                data={previewTableData ?? []}
                dataTestId={ELEMENT_DATA_TEST_IDS.SELECTED_TABLE_BODY}
                handleColorPicker={handleColorPicker}
                handleQuickColorPicker={handleQuickColorPicker}
                handleUpdateOrder={previewTableUpdateHandler}
                headerSortHandlers={previewHeaderSortHandlers}
                hideSortingTable={hideSortingTable}
                isLoading={isLoading}
                noDataFoundMessage={previewTableNoDataFoundMessage}
                overrideTableSorting={overrideTableSorting}
                showColorPicker={showColorPicker}
                showSideControls={showSideControls}
                sortingFns={sortingFns}
                sortingState={previewTableSortingState}
                TableHeader={previewTableHeader}
                TableHeaderButton={previewTableHeaderButton}
                tableInstanceRef={previewItemsTableInstanceRef}
                topPosition={topPosition}
                uniqueRowKey={previewRowUniqueKey}
                uniqueSelectedIds={previewTableUniqueSelectedIds}
              />
            </HStack>
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default PaginatedPreviewTable;
